import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CompletedLesson {
  id: string;
  lesson_topic: string;
  difficulty: string;
  completed_at: string;
}

interface QuizAttempt {
  id: string;
  difficulty: string;
  topic: string | null;
  question_count: number;
  score: number;
  attempted_at: string;
}

export const useCompletedLessons = (userId: string | null) => {
  const [completedLessons, setCompletedLessons] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Fetch completed lessons from database
  const fetchCompletedLessons = useCallback(async () => {
    if (!userId) {
      setCompletedLessons([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('completed_lessons')
        .select('lesson_topic')
        .eq('user_id', userId);

      if (error) throw error;

      setCompletedLessons(data?.map((l) => l.lesson_topic) || []);
    } catch (error) {
      console.error('Error fetching completed lessons:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Mark lesson as complete
  const markLessonComplete = useCallback(async (topic: string, difficulty: string = 'beginner') => {
    if (!userId) return false;

    try {
      const { error } = await supabase
        .from('completed_lessons')
        .upsert(
          {
            user_id: userId,
            lesson_topic: topic,
            difficulty,
            completed_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,lesson_topic' }
        );

      if (error) throw error;

      // Update local state
      setCompletedLessons((prev) => 
        prev.includes(topic) ? prev : [...prev, topic]
      );

      toast({
        title: 'Lesson Completed!',
        description: `"${topic}" has been marked as complete.`,
      });

      return true;
    } catch (error) {
      console.error('Error marking lesson complete:', error);
      toast({
        title: 'Error',
        description: 'Failed to save lesson progress.',
        variant: 'destructive',
      });
      return false;
    }
  }, [userId, toast]);

  useEffect(() => {
    fetchCompletedLessons();
  }, [fetchCompletedLessons]);

  return {
    completedLessons,
    isLoading,
    markLessonComplete,
    refreshLessons: fetchCompletedLessons,
  };
};

export const useQuizAttempts = (userId: string | null) => {
  const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Fetch quiz attempts
  const fetchAttempts = useCallback(async () => {
    if (!userId) {
      setAttempts([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('quiz_attempts')
        .select('*')
        .eq('user_id', userId)
        .order('attempted_at', { ascending: false });

      if (error) throw error;

      setAttempts(data || []);
    } catch (error) {
      console.error('Error fetching quiz attempts:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Save a quiz attempt
  const saveQuizAttempt = useCallback(async (
    difficulty: string,
    questionCount: number,
    score: number,
    topic?: string
  ) => {
    if (!userId) return false;

    try {
      const { error } = await (supabase.rpc as any)('record_quiz_attempt', {
        _difficulty: difficulty,
        _question_count: questionCount,
        _score: score,
        _topic: topic || null,
      });

      if (error) throw error;

      // Refresh attempts list
      await fetchAttempts();

      return true;
    } catch (error) {
      console.error('Error saving quiz attempt:', error);
      toast({
        title: 'Error',
        description: 'Failed to save quiz score.',
        variant: 'destructive',
      });
      return false;
    }
  }, [userId, fetchAttempts, toast]);

  // Get stats
  const getStats = useCallback(() => {
    if (attempts.length === 0) {
      return { totalAttempts: 0, averageScore: 0, bestScore: 0, totalQuestions: 0 };
    }

    const totalAttempts = attempts.length;
    const totalQuestions = attempts.reduce((sum, a) => sum + a.question_count, 0);
    const totalCorrect = attempts.reduce((sum, a) => sum + a.score, 0);
    const averageScore = Math.min(100, Math.round((totalCorrect / totalQuestions) * 100));
    const bestScore = Math.min(100, Math.max(...attempts.map((a) => Math.min(100, Math.round((a.score / a.question_count) * 100)))));

    return { totalAttempts, averageScore, bestScore, totalQuestions };
  }, [attempts]);

  useEffect(() => {
    fetchAttempts();
  }, [fetchAttempts]);

  return {
    attempts,
    isLoading,
    saveQuizAttempt,
    getStats,
    refreshAttempts: fetchAttempts,
  };
};
